import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  MessageCircle,
  Sparkles,
  Target,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import PublicNavbar from "@/components/landing/PublicNavbar";
import PublicFooter from "@/components/lgpd/Footer";
import PortfolioCarousel from "@/components/landing/PortfolioCarousel";
import EtlCasesSection from "@/components/landing/EtlCasesSection";
import PowerBiCasesSection from "@/components/landing/PowerBiCasesSection";
import AutomacaoCasesSection from "@/components/landing/AutomacaoCasesSection";
import { getServicoBySlug, servicos } from "@/data/servicos";

const WHATSAPP_PHONE = "5521998231720";

const buildWhatsAppUrl = (servicoNome: string) => {
  const msg = `Olá! Gostaria de solicitar um orçamento para o serviço de ${servicoNome}`;
  return `https://web.whatsapp.com/send?phone=${WHATSAPP_PHONE}&text=${encodeURIComponent(msg)}`;
};

const ServicoDetalhe = () => {
  const { slug } = useParams<{ slug: string }>();
  const servico = getServicoBySlug(slug);

  useEffect(() => {
    if (!servico) return;
    const prevTitle = document.title;
    document.title = `${servico.titulo} | itaCondo Sistemas`;

    const meta =
      document.querySelector<HTMLMetaElement>('meta[name="description"]') ||
      (() => {
        const m = document.createElement("meta");
        m.name = "description";
        document.head.appendChild(m);
        return m;
      })();
    const prevDesc = meta.content;
    meta.content = servico.tagline;

    return () => {
      document.title = prevTitle;
      meta.content = prevDesc;
    };
  }, [servico]);

  if (!servico) {
    return <Navigate to="/404" replace />;
  }

  const Icon = servico.icon;
  const whatsappUrl = buildWhatsAppUrl(servico.titulo);

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <PublicNavbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 overflow-hidden">
        <div className="absolute -top-[20%] -right-[10%] h-[600px] w-[600px] rounded-full bg-primary/20 blur-[120px] pointer-events-none" />
        <div className="absolute top-[40%] -left-[10%] h-[500px] w-[500px] rounded-full bg-blue-500/10 blur-[100px] pointer-events-none" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-slate-400 mb-8">
            <Link to="/" className="hover:text-white transition-colors">
              Home
            </Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/#outros-servicos" className="hover:text-white transition-colors">
              Serviços
            </Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-blue-300">{servico.titulo}</span>
          </nav>

          <div className="flex flex-col lg:flex-row items-start gap-10">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full mb-6">
                <Sparkles className="h-4 w-4 text-blue-400" />
                <span className="text-sm text-blue-300">Serviço Especializado</span>
              </div>

              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-blue-500/10 border border-blue-500/20 rounded-2xl flex items-center justify-center shrink-0">
                  <Icon className="h-8 w-8 text-blue-400" />
                </div>
                <h1 className="text-3xl lg:text-5xl font-bold leading-tight bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                  {servico.titulo}
                </h1>
              </div>

              <p className="text-lg text-slate-300 max-w-2xl leading-relaxed">
                {servico.tagline}
              </p>
            </div>

            {/* Price card */}
            <Card className="w-full lg:w-[360px] bg-slate-800/60 border-blue-500/30 backdrop-blur-sm shrink-0">
              <CardContent className="p-6">
                <p className="text-xs uppercase tracking-wider text-blue-300 mb-2">
                  Investimento
                </p>
                <p className="text-3xl font-bold text-white mb-1">{servico.preco}</p>
                <p className="text-sm text-slate-400 mb-6">{servico.precoDestaque}</p>

                <Button
                  asChild
                  size="lg"
                  className="w-full h-12 bg-green-500 hover:bg-green-600 text-white font-bold rounded-full shadow-lg shadow-green-500/30 hover:scale-[1.02] transition-all"
                >
                  <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="h-5 w-5 mr-2" />
                    Solicitar Proposta no WhatsApp
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Conteúdo */}
      <section className="py-16 lg:py-24 bg-slate-800/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-6">
            <ContentBlock
              icon={<CheckCircle2 className="h-6 w-6 text-blue-400" />}
              titulo="O que entregamos"
              items={servico.entregamos}
            />
            <ContentBlock
              icon={<Zap className="h-6 w-6 text-blue-400" />}
              titulo="Diferenciais Técnicos"
              items={servico.diferenciais}
            />
            <ContentBlock
              icon={<Target className="h-6 w-6 text-blue-400" />}
              titulo="Para quem é indicado"
              items={servico.indicado}
            />
          </div>
        </div>
      </section>

      {/* Portfólio (apenas Webdesign) */}
      {servico.slug === "webdesign" && <PortfolioCarousel />}

      {/* Cases (apenas ETL) */}
      {servico.slug === "etl" && <EtlCasesSection />}

      {/* Cases (apenas Power BI) */}
      {servico.slug === "power-bi" && <PowerBiCasesSection />}

      {/* Cases (apenas Automação) */}
      {servico.slug === "automacao" && <AutomacaoCasesSection />}

      {/* CTA final */}
      <section className="py-20 bg-slate-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl border border-blue-500/30 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 shadow-2xl">
            <div className="pointer-events-none absolute -top-32 -right-24 h-72 w-72 rounded-full bg-primary/30 blur-[120px]" />
            <div className="pointer-events-none absolute -bottom-32 -left-24 h-72 w-72 rounded-full bg-blue-500/20 blur-[120px]" />
            <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:48px_48px]" />

            <div className="relative p-8 lg:p-14 text-center">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-4 py-1.5">
                <Sparkles className="h-3.5 w-3.5 text-blue-300" />
                <span className="text-xs font-semibold uppercase tracking-wider text-blue-200">
                  Orçamento sem compromisso
                </span>
              </div>
              <h2 className="mx-auto mb-4 max-w-3xl text-2xl lg:text-4xl font-bold leading-tight text-white">
                Pronto para começar?
              </h2>
              <p className="text-slate-300 max-w-2xl mx-auto mb-8 text-base lg:text-lg">
                Fale agora com nosso time pelo WhatsApp e receba uma proposta
                personalizada para o seu projeto.
              </p>
              <Button
                asChild
                size="lg"
                className="h-14 bg-green-500 hover:bg-green-600 text-white font-bold rounded-full px-8 shadow-lg shadow-green-500/30 hover:scale-105 transition-all"
              >
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="h-5 w-5 mr-2" />
                  Solicitar Proposta no WhatsApp
                </a>
              </Button>
            </div>
          </div>

          {/* Outros serviços */}
          <div className="mt-16">
            <h3 className="text-xl font-semibold text-white mb-6 text-center">
              Conheça outros serviços
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {servicos
                .filter((s) => s.slug !== servico.slug)
                .slice(0, 3)
                .map((s) => {
                  const OtherIcon = s.icon;
                  return (
                    <Link
                      key={s.slug}
                      to={`/servicos/${s.slug}`}
                      className="group flex items-center gap-4 p-4 rounded-xl bg-slate-800/50 border border-white/10 hover:border-blue-500/40 hover:bg-slate-800/80 transition-all"
                    >
                      <div className="w-10 h-10 bg-blue-500/10 border border-blue-500/20 rounded-lg flex items-center justify-center shrink-0">
                        <OtherIcon className="h-5 w-5 text-blue-400" />
                      </div>
                      <span className="text-sm font-medium text-white flex-1 min-w-0 truncate">
                        {s.titulo}
                      </span>
                      <ArrowRight className="h-4 w-4 text-blue-300 shrink-0 transition-transform group-hover:translate-x-1" />
                    </Link>
                  );
                })}
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

const ContentBlock = ({
  icon,
  titulo,
  items,
}: {
  icon: React.ReactNode;
  titulo: string;
  items: string[];
}) => (
  <Card className="bg-slate-800/50 border-white/10 backdrop-blur-sm h-full">
    <CardContent className="p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 bg-blue-500/10 border border-blue-500/20 rounded-lg flex items-center justify-center shrink-0">
          {icon}
        </div>
        <h3 className="text-lg font-semibold text-white">{titulo}</h3>
      </div>
      <ul className="space-y-3">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
            <CheckCircle2 className="h-4 w-4 text-blue-400 mt-0.5 shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </CardContent>
  </Card>
);

export default ServicoDetalhe;